import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useAuditLog } from '../hooks/useAuditLog'
import MedicalRecordsSystem, {
  MEDICAL_RECORD_CATEGORIES,
  EnhancedMedicalRecord,
  RecordSearchFilters
} from '../lib/medicalRecordsSystem'
import MedicalHistoryViewer from './MedicalHistoryViewer'
import AdvancedMedicalForm from './AdvancedMedicalForm'
import MedicalDocumentUpload from './MedicalDocumentUpload'
import {
  FileText,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Archive,
  History,
  Upload,
  BarChart3,
  List,
  Grid,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Tag,
  Download,
  Share2,
  Stethoscope,
  Brain,
  Pill,
  TestTube,
  Microscope,
  Activity,
  Shield,
  Settings,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import { toast } from 'sonner'

interface EnhancedMedicalRecordsManagerProps {
  patientId?: string
  doctorId?: string
  mode: 'patient' | 'doctor'
  showCreateButton?: boolean
  defaultView?: 'records' | 'history' | 'create' | 'documents'
}

type ViewMode = 'records' | 'history' | 'create' | 'documents' | 'statistics'

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-800',
  normal: 'bg-blue-100 text-blue-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800'
}

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-800',
  active: 'bg-green-100 text-green-800',
  archived: 'bg-yellow-100 text-yellow-800',
  deleted: 'bg-red-100 text-red-800'
}

const CATEGORY_ICONS = {
  consultation: Stethoscope,
  diagnosis: Brain,
  prescription: Pill,
  lab_result: TestTube,
  imaging: Microscope,
  surgery: Activity,
  vaccination: Shield
}

const VIEW_MODES = [
  { id: 'records', label: 'Registros', icon: FileText, description: 'Lista de registros médicos' },
  { id: 'history', label: 'Histórico', icon: History, description: 'Timeline do histórico médico' },
  { id: 'documents', label: 'Documentos', icon: Upload, description: 'Documentos anexados' },
  { id: 'statistics', label: 'Estatísticas', icon: BarChart3, description: 'Análises e relatórios' }
] as const

export default function EnhancedMedicalRecordsManager({
  patientId,
  doctorId,
  mode,
  showCreateButton = true,
  defaultView = 'records'
}: EnhancedMedicalRecordsManagerProps) {
  const { user } = useAuth()
  const { logAudit } = useAuditLog()
  const [records, setRecords] = useState<EnhancedMedicalRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<RecordSearchFilters>({})
  const [selectedRecord, setSelectedRecord] = useState<EnhancedMedicalRecord | null>(null)
  const [currentView, setCurrentView] = useState<ViewMode>(defaultView)
  const [showFilters, setShowFilters] = useState(false)
  const [statistics, setStatistics] = useState<any>(null)
  const [editingRecord, setEditingRecord] = useState<EnhancedMedicalRecord | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  
  // Estados para navegação entre versões
  const [versionNavigation, setVersionNavigation] = useState<{
    previous: string | null
    next: string | null
    current: number
    total: number
  } | null>(null)
  const [loadingVersions, setLoadingVersions] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)

  useEffect(() => {
    loadRecords()
    loadStatistics()
  }, [patientId, doctorId, filters])

  // Função para carregar navegação entre versões - GENÉRICA
  const loadVersionNavigation = async (recordId: string) => {
    if (!user) return
    
    try {
      setLoadingVersions(true)
      console.log('🧭 [DEBUG] Carregando navegação para registro:', recordId)
      
      const { data: navigation, error } = await MedicalRecordsSystem.getVersionNavigation(recordId, user.id)
      
      if (error) {
        console.error('❌ [DEBUG] Erro ao carregar navegação de versões:', error)
        return
      }
      
      console.log('✅ [DEBUG] Navegação carregada:', navigation)
      setVersionNavigation(navigation)
    } catch (error) {
      console.error('Erro ao carregar navegação de versões:', error)
    } finally {
      setLoadingVersions(false)
    }
  }

  // Navegar para versão anterior - GENÉRICA
  const navigateToPreviousVersion = async () => {
    if (!versionNavigation?.previous || !user) return
    
    try {
      setLoadingVersions(true)
      console.log('🔙 [DEBUG] Navegando para versão anterior:', versionNavigation.previous)
      
      // Usar getRecordById para garantir descriptografia
      const { data: versionRecord, error } = await MedicalRecordsSystem.getRecordById(versionNavigation.previous, user.id, true)
      
      if (error || !versionRecord) {
        console.error('❌ [DEBUG] Erro ao carregar versão anterior:', error)
        toast.error('Erro ao carregar versão anterior')
        return
      }
      
      console.log('✅ [DEBUG] Versão anterior carregada com dados descriptografados:', {
        id: versionRecord.id,
        version: versionRecord.version,
        hasDescription: !!versionRecord.description,
        hasDiagnosis: !!versionRecord.diagnosis,
        hasTreatment: !!versionRecord.treatment
      })
      setSelectedRecord(versionRecord)
      
      // Recarregar navegação para a nova versão
      console.log('🔄 [DEBUG] Recarregando navegação para versão:', versionRecord.id)
      await loadVersionNavigation(versionRecord.id)
      
      toast.success('Navegou para versão anterior')
    } catch (error) {
      console.error('Erro ao navegar para versão anterior:', error)
      toast.error('Erro ao navegar para versão anterior')
    } finally {
      setLoadingVersions(false)
    }
  }

  // Navegar para próxima versão - GENÉRICA
  const navigateToNextVersion = async () => {
    if (!versionNavigation?.next || !user) return
    
    try {
      setLoadingVersions(true)
      console.log('🔜 [DEBUG] Navegando para próxima versão:', versionNavigation.next)
      
      // Usar getRecordById para garantir descriptografia
      const { data: versionRecord, error } = await MedicalRecordsSystem.getRecordById(versionNavigation.next, user.id, true)
      
      if (error || !versionRecord) {
        console.error('❌ [DEBUG] Erro ao carregar próxima versão:', error)
        toast.error('Erro ao carregar próxima versão')
        return
      }
      
      console.log('✅ [DEBUG] Próxima versão carregada com dados descriptografados:', {
        id: versionRecord.id,
        version: versionRecord.version,
        hasDescription: !!versionRecord.description,
        hasDiagnosis: !!versionRecord.diagnosis,
        hasTreatment: !!versionRecord.treatment
      })
      setSelectedRecord(versionRecord)
      
      // Recarregar navegação para a nova versão
      console.log('🔄 [DEBUG] Recarregando navegação para versão:', versionRecord.id)
      await loadVersionNavigation(versionRecord.id)
      
      toast.success('Navegou para próxima versão')
    } catch (error) {
      console.error('Erro ao navegar para próxima versão:', error)
      toast.error('Erro ao navegar para próxima versão')
    } finally {
      setLoadingVersions(false)
    }
  }

  const loadRecords = async (showRefresh = false) => {
    if (!user) return

    try {
      if (showRefresh) {
        setRefreshing(true)
      } else {
        setLoading(true)
      }
      
      const searchFilters: RecordSearchFilters = {
        ...filters,
        patient_id: mode === 'patient' ? user.id : patientId,
        doctor_id: mode === 'doctor' ? user.id : doctorId
      }

      const { data, error } = await MedicalRecordsSystem.searchRecords(searchFilters, user.id)
      
      console.log('🔍 [DEBUG] Resultado da busca de registros:', {
        mode,
        searchFilters,
        dataCount: data?.length || 0,
        error,
        userId: user.id
      })
      
      if (error) {
        toast.error('Erro ao carregar registros médicos')
        console.error('❌ [ERROR] Erro ao carregar registros:', error)
      } else {
        // Log dos dados brutos recebidos
        console.log('📋 [DEBUG] Dados brutos recebidos:', data?.map(record => ({
          id: record.id,
          title: record.title,
          description: record.description,
          diagnosis: record.diagnosis,
          treatment: record.treatment,
          tags: record.tags,
          priority: record.priority,
          status: record.status,
          custom_fields: record.custom_fields,
          is_encrypted: record.is_encrypted
        })))
        
        // Filtrar por termo de busca
        let filteredData = data || []
        if (searchTerm) {
          filteredData = filteredData.filter(record =>
            record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.diagnosis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            record.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
          )
        }
        
        // Filtrar apenas versões atuais ativas
        const uniqueActiveRecords = filteredData.filter(record => 
          record.status === 'active' && (record.is_current_version === true || record.is_current_version === null)
        )
        
        console.log('🔎 [DEBUG] Dados após filtro de busca e versões únicas:', {
          searchTerm,
          originalCount: data?.length || 0,
          filteredCount: filteredData.length,
          uniqueActiveCount: uniqueActiveRecords.length,
          uniqueActiveRecords: uniqueActiveRecords.map(record => ({
            id: record.id,
            title: record.title,
            record_type: record.record_type,
            status: record.status,
            is_current_version: record.is_current_version
          }))
        })
        
        setRecords(uniqueActiveRecords)
        console.log('✅ [DEBUG] Registros únicos ativos definidos no estado:', uniqueActiveRecords.length)
        
        // Log de auditoria para visualização de registros
        if (filteredData.length > 0) {
          await logAudit({
            action_type: 'view',
            resource_type: 'medical_record',
            resource_description: `Visualização de ${filteredData.length} registros médicos`,
            metadata: {
              search_term: searchTerm,
              filters: filters,
              mode: mode,
              patient_id: patientId,
              doctor_id: doctorId
            }
          })
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar registros médicos')
      console.error(error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const loadStatistics = async () => {
    if (!user) return

    try {
      const { data, error } = await MedicalRecordsSystem.getRecordStatistics(user.id, mode)
      if (!error && data) {
        setStatistics(data)
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error)
    }
  }

  const handleRecordSaved = async (record: EnhancedMedicalRecord) => {
    await loadRecords()
    await loadStatistics()
    setCurrentView('records')
    setEditingRecord(null)
    toast.success('Registro salvo com sucesso')
  }

  const handleEditRecord = async (record: EnhancedMedicalRecord) => {
    if (!user) return
    
    try {
      console.log('🔧 [DEBUG] Iniciando edição do registro:', record.id)
      
      // Carregar registro completo com descriptografia
      const { data: fullRecord, error } = await MedicalRecordsSystem.getRecordById(record.id, user.id)
      
      if (error || !fullRecord) {
        toast.error('Erro ao carregar dados do registro para edição')
        console.error('❌ [ERROR] Erro ao carregar registro para edição:', error)
        return
      }
      
      console.log('📋 [DEBUG] Dados descriptografados carregados para edição:', {
        id: fullRecord.id,
        title: fullRecord.title,
        description: fullRecord.description,
        diagnosis: fullRecord.diagnosis,
        treatment: fullRecord.treatment,
        patient_id: fullRecord.patient_id,
        custom_fields: fullRecord.custom_fields,
        record_type: fullRecord.record_type,
        is_encrypted: fullRecord.is_encrypted
      })
      
      setEditingRecord(fullRecord)
      setShowEditModal(true)
      
      // Log de auditoria para edição de registro
      await logAudit({
        action_type: 'update',
        resource_type: 'medical_record',
        resource_id: fullRecord.id,
        resource_description: `Iniciando edição do registro: ${fullRecord.title}`,
        metadata: {
          record_type: fullRecord.record_type,
          priority: fullRecord.priority,
          status: fullRecord.status,
          was_encrypted: fullRecord.is_encrypted
        }
      })
      
      toast.success('Dados carregados para edição')
    } catch (error) {
      console.error('❌ [ERROR] Erro ao preparar edição:', error)
      toast.error('Erro ao preparar edição do registro')
    }
  }

  const handleArchiveRecord = async (recordId: string) => {
    if (!user) return

    try {
      // Buscar dados do registro antes de arquivar
      const recordToArchive = records.find(r => r.id === recordId)
      
      const { error } = await MedicalRecordsSystem.archiveRecord(recordId, user.id)
      
      if (error) {
        toast.error('Erro ao arquivar registro')
        return
      }

      // Log de auditoria para arquivamento
      await logAudit({
        action_type: 'delete',
        resource_type: 'medical_record',
        resource_id: recordId,
        resource_description: `Registro arquivado: ${recordToArchive?.title || 'Registro médico'}`,
        metadata: {
          record_type: recordToArchive?.record_type,
          priority: recordToArchive?.priority,
          archived_by: user.id,
          archive_reason: 'Manual archive'
        }
      })

      toast.success('Registro arquivado com sucesso')
      await loadRecords()
      await loadStatistics()
    } catch (error) {
      toast.error('Erro ao arquivar registro')
      console.error(error)
    }
  }

  const getCategoryIcon = (recordType: string) => {
    const IconComponent = CATEGORY_ICONS[recordType as keyof typeof CATEGORY_ICONS] || FileText
    return <IconComponent className="h-4 w-4" />
  }

  const getCategory = (recordType: string) => {
    return MEDICAL_RECORD_CATEGORIES.find(c => c.id === recordType)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'critical': return <AlertTriangle className="h-4 w-4 text-red-600" />
      case 'high': return <AlertTriangle className="h-4 w-4 text-orange-600" />
      case 'normal': return <CheckCircle className="h-4 w-4 text-blue-600" />
      case 'low': return <CheckCircle className="h-4 w-4 text-gray-600" />
      default: return <CheckCircle className="h-4 w-4 text-gray-600" />
    }
  }

  const refreshData = () => {
    loadRecords(true)
    loadStatistics()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header com estatísticas resumidas */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Recentes</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.recent}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-orange-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Alta Prioridade</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.byPriority?.high || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Críticos</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.byPriority?.critical || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navegação entre views */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Tabs de navegação */}
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            {VIEW_MODES.map((viewMode) => {
              const IconComponent = viewMode.icon
              return (
                <button
                  key={viewMode.id}
                  onClick={() => setCurrentView(viewMode.id as ViewMode)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentView === viewMode.id
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title={viewMode.description}
                >
                  <IconComponent className="h-4 w-4" />
                  <span className="hidden sm:inline">{viewMode.label}</span>
                </button>
              )
            })}
            {mode === 'doctor' && (
              <button
                onClick={() => {
                  setEditingRecord(null)
                  setShowCreateModal(true)
                }}
                className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors text-gray-600 hover:text-gray-900"
                title="Criar novo registro"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Novo</span>
              </button>
            )}
          </div>

          {/* Controles */}
          <div className="flex items-center space-x-2">
            <button
              onClick={refreshData}
              disabled={refreshing}
              className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Atualizar dados"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Atualizar</span>
            </button>
            
            {currentView === 'records' && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Filter className="h-4 w-4" />
                <span className="hidden sm:inline">Filtros</span>
              </button>
            )}
          </div>
        </div>

        {/* Busca e filtros para view de registros */}
        {currentView === 'records' && (
          <div className="mt-4">
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar registros médicos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filtros expandidos */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                    <select
                      value={filters.record_type || ''}
                      onChange={(e) => setFilters({ ...filters, record_type: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Todas as categorias</option>
                      {MEDICAL_RECORD_CATEGORIES.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prioridade</label>
                    <select
                      value={filters.priority || ''}
                      onChange={(e) => setFilters({ ...filters, priority: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Todas as prioridades</option>
                      <option value="low">Baixa</option>
                      <option value="normal">Normal</option>
                      <option value="high">Alta</option>
                      <option value="critical">Crítica</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={filters.status || ''}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Todos os status</option>
                      <option value="draft">Rascunho</option>
                      <option value="active">Ativo</option>
                      <option value="archived">Arquivado</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                    <input
                      type="date"
                      value={filters.date_from || ''}
                      onChange={(e) => setFilters({ ...filters, date_from: e.target.value || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conteúdo principal baseado na view selecionada */}
      {currentView === 'records' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Registros Médicos ({records.length})
            </h2>
          </div>
          <div className="p-6">
            {records.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {searchTerm || Object.keys(filters).length > 0
                    ? 'Nenhum registro encontrado com os filtros aplicados'
                    : 'Nenhum registro médico encontrado'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {records.map((record) => {
                  const category = getCategory(record.record_type)
                  return (
                    <div key={record.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="flex items-center space-x-1">
                              {getCategoryIcon(record.record_type)}
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${category?.color || 'bg-gray-100'} text-white`}>
                                {category?.name || record.record_type}
                              </span>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[record.priority as keyof typeof PRIORITY_COLORS]}`}>
                              {record.priority === 'low' ? 'Baixa' :
                               record.priority === 'normal' ? 'Normal' :
                               record.priority === 'high' ? 'Alta' : 'Crítica'}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[record.status as keyof typeof STATUS_COLORS]}`}>
                              {record.status === 'draft' ? 'Rascunho' :
                               record.status === 'active' ? 'Ativo' :
                               record.status === 'archived' ? 'Arquivado' : 'Excluído'}
                            </span>
                            {record.is_encrypted && (
                              <Shield className="h-4 w-4 text-green-600" />
                            )}
                            {getPriorityIcon(record.priority)}
                          </div>
                          <h3 className="font-medium text-gray-900 mb-1">{record.title}</h3>
                          <p className="text-sm text-gray-600 mb-2">{record.description}</p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            {mode === 'doctor' && (
                              <span>Paciente: {record.patient?.full_name}</span>
                            )}
                            {mode === 'patient' && (
                              <span>Dr. {record.doctor?.full_name}</span>
                            )}
                            <span>{formatDate(record.created_at)}</span>
                            {record.version && record.version > 1 && (
                              <span>v{record.version}</span>
                            )}
                          </div>
                          {record.tags && record.tags.length > 0 && (
                            <div className="flex items-center space-x-1 mt-2">
                              <Tag className="h-3 w-3 text-gray-400" />
                              {record.tags.map((tag, index) => (
                                <span key={index} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={async () => {
                              if (!user) return
                              
                              try {
                                // Carregar registro completo com descriptografia
                                const { data: fullRecord, error } = await MedicalRecordsSystem.getRecordById(record.id, user.id)
                                
                                if (error || !fullRecord) {
                                  toast.error('Erro ao carregar detalhes do registro')
                                  return
                                }
                                
                                setSelectedRecord(fullRecord)
                                
                                // Carregar navegação de versões para qualquer tipo de registro que tenha versões
                                if (fullRecord.version && fullRecord.version > 1) {
                                  await loadVersionNavigation(fullRecord.id)
                                }
                              } catch (error) {
                                console.error('Erro ao carregar detalhes:', error)
                                toast.error('Erro ao carregar detalhes do registro')
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                            title="Ver detalhes"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          {mode === 'doctor' && (
                            <>
                              <button
                                onClick={() => handleEditRecord(record)}
                                className="text-gray-600 hover:text-gray-800 transition-colors"
                                title="Editar"
                              >
                                <Edit className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleArchiveRecord(record.id)}
                                className="text-orange-600 hover:text-orange-800 transition-colors"
                                title="Arquivar"
                              >
                                <Archive className="h-5 w-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {currentView === 'history' && patientId && (
        <MedicalHistoryViewer
          patientId={patientId}
          mode={mode}
          showTimeline={true}
          showStatistics={true}
          showFilters={true}
        />
      )}

      {/* Removido: view 'create' agora é um modal */}

      {currentView === 'documents' && patientId && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Documentos Médicos</h2>
          <MedicalDocumentUpload
            patientId={patientId}
            onDocumentUploaded={(doc) => {
              toast.success('Documento enviado com sucesso')
            }}
          />
        </div>
      )}

      {currentView === 'statistics' && (
        <MedicalHistoryViewer
          patientId={patientId || user?.id || ''}
          mode={mode}
          showTimeline={false}
          showStatistics={true}
          showFilters={false}
        />
      )}

      {/* Modal de detalhes do registro */}
      {selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Detalhes do Registro</h3>
                <button
                  onClick={() => {
                    setSelectedRecord(null)
                    setVersionNavigation(null)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Navegação entre Versões - DISPONÍVEL PARA TODOS OS TIPOS */}
            {versionNavigation && versionNavigation.total > 1 && (
              <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <History className="h-5 w-5 text-blue-600" />
                    <div>
                      <div className="font-medium text-blue-900">
                        Versão {versionNavigation.current} de {versionNavigation.total}
                      </div>
                      <div className="text-sm text-blue-700">
                        {selectedRecord.is_current_version !== false ? (
                          <span className="inline-flex items-center gap-1">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-green-700 font-medium">Versão Atual</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <Clock className="h-4 w-4 text-yellow-600" />
                            <span className="text-yellow-700 font-medium">Versão Histórica</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={navigateToPreviousVersion}
                      disabled={!versionNavigation.previous || loadingVersions}
                      className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </button>
                    
                    <button
                      onClick={navigateToNextVersion}
                      disabled={!versionNavigation.next || loadingVersions}
                      className="flex items-center gap-1 px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Próxima
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                {/* Barra de progresso */}
                <div className="mt-3">
                  <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${Math.min(100, Math.max(0, ((versionNavigation.current - 1) / Math.max(versionNavigation.total - 1, 1)) * 100))}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-4">
                  {getCategoryIcon(selectedRecord.record_type)}
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCategory(selectedRecord.record_type)?.color || 'bg-gray-100'} text-white`}>
                    {getCategory(selectedRecord.record_type)?.name || selectedRecord.record_type}
                  </span>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${PRIORITY_COLORS[selectedRecord.priority as keyof typeof PRIORITY_COLORS]}`}>
                    Prioridade: {selectedRecord.priority === 'low' ? 'Baixa' :
                                selectedRecord.priority === 'normal' ? 'Normal' :
                                selectedRecord.priority === 'high' ? 'Alta' : 'Crítica'}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título</label>
                  <p className="text-gray-900">{selectedRecord.title}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <p className="text-gray-900">{selectedRecord.description || 'Não informado'}</p>
                </div>
                
                {selectedRecord.diagnosis && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diagnóstico</label>
                    <p className="text-gray-900">{selectedRecord.diagnosis}</p>
                  </div>
                )}
                
                {selectedRecord.treatment && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tratamento</label>
                    <p className="text-gray-900">{selectedRecord.treatment}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Paciente</label>
                    <p className="text-gray-900">{selectedRecord.patient?.full_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Médico</label>
                    <p className="text-gray-900">Dr. {selectedRecord.doctor?.full_name}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Criação</label>
                    <p className="text-gray-900">{formatDate(selectedRecord.created_at)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Versão</label>
                    <div className="flex items-center gap-2">
                      <p className="text-gray-900">v{selectedRecord.version || 1}</p>
                      {selectedRecord.is_current_version !== false ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Atual</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Histórica</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {selectedRecord.tags && selectedRecord.tags.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedRecord.tags.map((tag, index) => (
                        <span key={index} className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {selectedRecord.blockchain_hash && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hash Blockchain</label>
                    <p className="text-gray-900 font-mono text-sm break-all">{selectedRecord.blockchain_hash}</p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setSelectedRecord(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Fechar
                </button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                  <Download className="h-4 w-4" />
                  <span>Baixar</span>
                </button>
                <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2">
                  <Share2 className="h-4 w-4" />
                  <span>Compartilhar</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de criação de novo registro */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            <AdvancedMedicalForm
              patientId={mode === 'patient' ? user?.id : patientId}
              doctorId={doctorId || user?.id}
              mode="create"
              allowPatientSelection={mode === 'doctor'}
              onSave={async (record) => {
                 setShowCreateModal(false)
                 await handleRecordSaved(record)
               }}
              onCancel={() => {
                setShowCreateModal(false)
              }}
            />
          </div>
        </div>
      )}

      {/* Modal de edição */}
      {showEditModal && editingRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            <AdvancedMedicalForm
              patientId={editingRecord.patient_id}
              doctorId={editingRecord.doctor_id}
              recordId={editingRecord.id}
              mode="edit"
              initialData={editingRecord}
              allowPatientSelection={false}
              onSave={async (record) => {
                 setShowEditModal(false)
                 setEditingRecord(null)
                 await handleRecordSaved(record)
               }}
              onCancel={() => {
                setShowEditModal(false)
                setEditingRecord(null)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}